import type { AnySchema } from 'yup';
import type { ReportTemplatePageMarginInput } from '@/graphql/types';
import {
    ReportTemplatePageMarginUnit,
    ReportTemplatePageOrientation,
    ReportTemplatePageSize,
} from '@/graphql/types';
import { schema } from '../../../schema';
import { isRecord } from '../../../utils/typeGuards';
import {
    isReportTemplatePageMarginUnit,
    isReportTemplatePageOrientation,
    isReportTemplatePageSize,
} from './typeGuards';

/**
 * Aliased from the generated mutation input so the editor, runtime, export payload, and API cannot
 * disagree about the pair.
 */
export type PageMargin = ReportTemplatePageMarginInput;

export interface PageSettings {
    pageSize: ReportTemplatePageSize;
    pageOrientation: ReportTemplatePageOrientation;
    pageMargin: PageMargin;
}

interface ReportPageProfileCss extends PageSettings {
    pageName: string;
    pageClassName: string;
}

interface ReportPageProfilesInput {
    defaultProfile: PageSettings;
    profiles: readonly ReportPageProfileCss[];
}

type PageSizeCssMap = Record<ReportTemplatePageSize, Record<ReportTemplatePageOrientation, string>>;

const PAGE_SIZE_CSS_MAP: PageSizeCssMap = {
    [ReportTemplatePageSize.A4]: {
        [ReportTemplatePageOrientation.Portrait]: '21cm 29.7cm',
        [ReportTemplatePageOrientation.Landscape]: '29.7cm 21cm',
    },
    [ReportTemplatePageSize.Letter]: {
        [ReportTemplatePageOrientation.Portrait]: '8.5in 11in',
        [ReportTemplatePageOrientation.Landscape]: '11in 8.5in',
    },
};

const PAGE_MARGIN_CSS_UNITS: Record<ReportTemplatePageMarginUnit, string> = {
    [ReportTemplatePageMarginUnit.Cm]: 'cm',
    [ReportTemplatePageMarginUnit.In]: 'in',
};

const PAGE_MARGIN_MAX_VALUE_BY_UNIT: Readonly<Record<ReportTemplatePageMarginUnit, number>> =
    schema.reportTemplatePageMargin.maxValueByUnit;

const PAGE_MARGIN_MIN_VALUE: number = schema.reportTemplatePageMargin.minValue;

const PAGE_MARGIN_DECIMAL_PLACES: number = schema.reportTemplatePageMargin.decimalPlaces;

export const DEFAULT_REPORT_TEMPLATE_PAGE_MARGIN: PageMargin = Object.freeze({
    value: 1.5,
    unit: ReportTemplatePageMarginUnit.Cm,
});

export const DEFAULT_REPORT_TEMPLATE_PAGE_SETTINGS: PageSettings = Object.freeze({
    pageSize: ReportTemplatePageSize.A4,
    pageOrientation: ReportTemplatePageOrientation.Portrait,
    pageMargin: DEFAULT_REPORT_TEMPLATE_PAGE_MARGIN,
});

export function getPageMarginMaxValue(unit: ReportTemplatePageMarginUnit): number {
    return PAGE_MARGIN_MAX_VALUE_BY_UNIT[unit];
}

function isPageMarginValueWithinBounds(
    value: unknown,
    unit: ReportTemplatePageMarginUnit
): value is number {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return false;
    }

    return value >= PAGE_MARGIN_MIN_VALUE && value <= getPageMarginMaxValue(unit);
}

function hasSupportedPageMarginDecimals(value: number): boolean {
    return Number(value.toFixed(PAGE_MARGIN_DECIMAL_PLACES)) === value;
}

/**
 * The whole value contract: within the bound of its unit and storable in `page_margin_value`. The
 * only yes/no answer exposed, so no caller can accept a margin the settings form or the backend
 * rejects.
 */
export function isSupportedPageMarginValue(
    value: unknown,
    unit: ReportTemplatePageMarginUnit
): value is number {
    return isPageMarginValueWithinBounds(value, unit) && hasSupportedPageMarginDecimals(value);
}

export function getPageSizeCss(
    pageSize: ReportTemplatePageSize,
    pageOrientation: ReportTemplatePageOrientation
): string {
    // The map covers the enums, so a miss means a value reached the runtime without passing a
    // parse boundary; name it here instead of letting the undefined travel on.
    const pageSizeCss: string | undefined = PAGE_SIZE_CSS_MAP[pageSize]?.[pageOrientation];
    if (!pageSizeCss) {
        throw new Error(
            `Unsupported page size/orientation: size=${String(pageSize)} ` +
                `orientation=${String(pageOrientation)}`
        );
    }

    return pageSizeCss;
}

export function pageMarginToCss({ value, unit }: PageMargin): string {
    return `${value}${PAGE_MARGIN_CSS_UNITS[unit]}`;
}

/**
 * Validator for a margin value whose bound depends on a sibling unit field. The sibling name
 * differs per form (`pageMarginUnit` in the template form, `unit` in the per-page margin group),
 * so callers pass it in while the bound stays owned here.
 */
export function buildPageMarginValueValidationSchema(
    unitFieldName: string
): (baseSchema: AnySchema) => AnySchema {
    return function buildValidationSchema(baseSchema: AnySchema): AnySchema {
        return baseSchema.test(
            'page-margin-value-within-bounds',
            // Replaced below with the rule the value broke.
            'Unsupported page margin value',
            function test(value) {
                if (value === null || typeof value === 'undefined') {
                    // Absent means "inherit"; the field's `required` flag decides if that is legal.
                    return true;
                }

                const unit = isRecord(this.parent) ? this.parent[unitFieldName] : undefined;
                if (!isReportTemplatePageMarginUnit(unit)) {
                    // The unit field reports its own error; do not report it twice.
                    return true;
                }

                if (!isPageMarginValueWithinBounds(value, unit)) {
                    return this.createError({
                        message: interpolate(
                            gettext('PAGE_MARGIN_RANGE_VALIDATION'),
                            {
                                minValue: PAGE_MARGIN_MIN_VALUE,
                                maxValue: getPageMarginMaxValue(unit),
                                unit: PAGE_MARGIN_CSS_UNITS[unit],
                            },
                            true
                        ),
                    });
                }

                if (!hasSupportedPageMarginDecimals(value)) {
                    return this.createError({
                        message: interpolate(
                            gettext('PAGE_MARGIN_DECIMALS_VALIDATION'),
                            { decimalPlaces: PAGE_MARGIN_DECIMAL_PLACES },
                            true
                        ),
                    });
                }

                return true;
            }
        );
    };
}

export function parsePageMargin(value: unknown, fieldLabel: string): PageMargin {
    if (!isRecord(value)) {
        throw new Error(`Expected "${fieldLabel}" to be an object.`);
    }

    const { unit } = value;
    if (!isReportTemplatePageMarginUnit(unit)) {
        throw new Error(`Unsupported "${fieldLabel}.unit" value "${String(unit)}".`);
    }

    const marginValue = value.value;
    if (!isSupportedPageMarginValue(marginValue, unit)) {
        throw new Error(
            `Expected "${fieldLabel}.value" to be a number between ${PAGE_MARGIN_MIN_VALUE} and ` +
                `${getPageMarginMaxValue(unit)} with at most ${PAGE_MARGIN_DECIMAL_PLACES} ` +
                `decimals, received "${String(marginValue)}".`
        );
    }

    return { value: marginValue, unit };
}

export function parsePageSettings(value: unknown, fieldPrefix: string): PageSettings {
    if (!isRecord(value)) {
        throw new Error(`Expected "${fieldPrefix}" to be an object.`);
    }

    const { pageSize, pageOrientation } = value;
    if (!isReportTemplatePageSize(pageSize)) {
        throw new Error(`Unsupported "${fieldPrefix}.pageSize" value "${String(pageSize)}".`);
    }

    if (!isReportTemplatePageOrientation(pageOrientation)) {
        throw new Error(
            `Unsupported "${fieldPrefix}.pageOrientation" value "${String(pageOrientation)}".`
        );
    }

    return {
        pageSize,
        pageOrientation,
        pageMargin: parsePageMargin(value.pageMargin, `${fieldPrefix}.pageMargin`),
    };
}

function applyReportPageSettingsCss(css: string): void {
    const styleId = 'report-page-size-style';
    let style = document.getElementById(styleId);
    if (!style) {
        style = document.createElement('style');
        style.id = styleId;
        document.head.appendChild(style);
    }

    style.textContent = css;
}

export function applyReportPageProfiles({
    defaultProfile,
    profiles,
}: ReportPageProfilesInput): void {
    const profileCssBlocks = profiles
        .map(
            (profile) => `
@page ${profile.pageName} {
    padding: 0;
    margin: ${pageMarginToCss(profile.pageMargin)};
    size: ${getPageSizeCss(profile.pageSize, profile.pageOrientation)};
}

.${profile.pageClassName} {
    page: ${profile.pageName};
}
`
        )
        .join('\n');

    const css = `
@page {
    padding: 0;
    margin: ${pageMarginToCss(defaultProfile.pageMargin)};
    size: ${getPageSizeCss(defaultProfile.pageSize, defaultProfile.pageOrientation)};
}

.report-page:not(:last-child) {
    break-after: page;
}
${profileCssBlocks}
`;

    applyReportPageSettingsCss(css);
}
